<template>
    <div>
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        height="40px"
                        :placeholder="$vuetify.lang.t('$vuetify.groupHoliday.searchBy')"
                        solo
                        @keyup.enter="getGroupHolidayPagination()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchGroupHoliday()"
                        height="40px"
                        color="primary"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.groupHoliday.search') }}
                    </v-btn>
                </div>

                <div class="year-input">
                    <v-menu
                        ref="menu"
                        :close-on-content-click="false"
                        v-model="menu"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                slot="activator"
                                v-model="displayYear"
                                prepend-inner-icon="event"
                                outlined
                                @click:prepend-inner="menu = true"
                                v-on="on"
                                height="40"
                                background-color="#ffffff"
                                readonly>
                            </v-text-field>
                        </template>
                        <v-date-picker
                            ref="picker"
                            :locale="localeLang"
                            v-model="year"
                            :max="maxDate"
                            @input="save"
                            reactive
                            no-title>
                        </v-date-picker>
                    </v-menu>
                </div>
            </div>
            <div>
                <v-btn
                    v-if="permissionCreate"
                    height="40px"
                    :disabled="!branch"
                    @click="createGroupHoliday()"
                    color="primary"> + {{ $vuetify.lang.t('$vuetify.groupHoliday.addGroupHoliday') }} </v-btn>
            </div>
        </div>
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalGroupHoliday"
                :items="groupHolidays"
                :loading="loading">
                <template
                    v-if="permissionUpdate || permissionDelete"
                    v-slot:[`item.action`]="{ item }">
                    <div class="action">
                        <v-icon
                            v-if="permissionUpdate && passedDate(item)"
                            class="mr-2"
                            @click="editGroupHoliday(item.id)">
                            edit
                        </v-icon>
                        <v-icon
                            v-if="permissionDelete && passedDate(item)"
                            class="mr-2"
                            @click="openModalDelete(item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>

        <div
            v-if="companyHolidays.length > 0"
            class="company-holiday-header py-4">
            {{ $vuetify.lang.t('$vuetify.groupHoliday.companyHoliday') }}
        </div>

        <v-card v-if="companyHolidays.length > 0" class="content-container">
            <v-simple-table>
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th class="text-left">
                                {{ $vuetify.lang.t('$vuetify.groupHoliday.groupHolidayName') }}
                            </th>
                            <th class="text-left">
                                {{ $vuetify.lang.t('$vuetify.groupHoliday.holiday') }}
                            </th>
                            <th class="text-left">
                                {{ $vuetify.lang.t('$vuetify.groupHoliday.dateCreate') }}
                            </th>
                            <th class="text-left">
                                {{ $vuetify.lang.t('$vuetify.groupHoliday.description') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in companyHolidays"
                            :key="item.id">
                            <td style="width: 20%">{{ item.name }}</td>
                            <td style="width: 15%">{{ item.date }}</td>
                            <td style="width: 15%">{{ item.created_at }}</td>
                            <td style="width: 50%">{{ item.description }}</td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
        </v-card>

        <group-holiday-form-modal
            ref="groupHolidayFormModal"
            @success="getGroupHolidayPagination()"
            @modal-closed="groupHolidayFormModalIsClosed()"
            :groupHolidayId="groupHolidayId"
            :branchId="branch"
            :min="minDate"
            :mode="mode"/>

        <delete-modal
            ref="modalDeleteGroup"
            @success-deleted="deleteSuccess()"
            :deleteForm="deleteForm"/>
    </div>
</template>

<script>
import GroupHolidayFormModal from '@/components/modal/GroupHolidayFormModal'
import DeleteModal from '@/components/modal/DeleteModal'
import permissionConstant from '@/constants/permissions.js'

export default {
    name: 'GroupHoliday',

    components: {
        GroupHolidayFormModal,
        DeleteModal
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.groupHoliday.groupHolidayName'), value: 'name', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.groupHoliday.holiday'), value: 'date', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.groupHoliday.dateCreate'), value: 'created_at', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.groupHoliday.forGroup'), value: 'for_group', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.groupHoliday.description'), value: 'description', width: '20%' },
                { text: '', value: 'action', width: '10%', sortable: false }
            ]
        },
        questionDelete () {
            return this.$vuetify.lang.t('$vuetify.groupHoliday.questionDelete')
        },
        msgDeleteSuccess () {
            return {
                title: this.$vuetify.lang.t('$vuetify.groupHoliday.congratulation'),
                text: this.$vuetify.lang.t('$vuetify.groupHoliday.msgDeleteSuccess'),
                type: 'success'
            }
        }
    },
    data () {
        return {
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            companyHolidays: [],
            groupHolidays: [],
            totalGroupHoliday: 0,
            loading: false,
            branch: null,
            permissionCreate: true,
            permissionUpdate: true,
            permissionDelete: true,
            menu: false,
            year: this.$util.getCurrentDate(),
            maxDate: this.$util.getNextYear(2),
            displayYear: this.yearFormat(this.$util.getCurrentDate()),
            mode: '',
            groupHolidayId: null,
            minDate: null,
            deleteForm: {
                header: '',
                content: '',
                path: ''
            }
        }
    },

    methods: {
        createGroupHoliday () {
            this.mode = 'create'
            this.$refs.groupHolidayFormModal.openModal()
        },

        editGroupHoliday (groupHolidayId) {
            this.mode = 'update'
            this.groupHolidayId = groupHolidayId
            this.$refs.groupHolidayFormModal.openModal()
        },

        openModalDelete (groupHolidayId) {
            this.deleteForm.header = this.$vuetify.lang.t('$vuetify.groupHoliday.deleteGroup')
            this.deleteForm.content = this.questionDelete.replace('$Name', groupHolidayId.name)
            this.deleteForm.path = `group/holiday/delete/${groupHolidayId.id}`
            this.$refs.modalDeleteGroup.dialog = true
        },

        async deleteSuccess () {
            this.$notify(this.msgDeleteSuccess)
            await this.getGroupHolidayPagination()
        },

        groupHolidayFormModalIsClosed () {
            this.mode = ''
            this.groupHolidayId = null
        },

        async getCompanyHolidays () {
            const year = new Date().getFullYear()
            const res = await this.axios.get(`company/holiday/get-all?year=${year}`)
            if (res && res.data && res.data.status === 'ok') {
                this.companyHolidays = res.data.data
            }
        },

        async searchGroupHoliday () {
            this.options.page = 1
            await this.getGroupHolidayPagination()
        },

        async getGroupHolidayPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            const year = this.getYear()

            let url = `group/holidays?page=${page}&itemsPerPage=${itemsPerPage}&year=${year}&branch=${this.branch}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.groupHolidays = res.data.data
                this.totalGroupHoliday = res.data.total
                this.loading = false
            }
        },

        async save (date) {
            this.$refs.menu.save(date)
            this.$refs.picker.activePicker = 'YEAR'
            this.menu = false

            await this.getGroupHolidayPagination()
        },

        getYear () {
            const [year] = this.year.split('-')
            return parseInt(year)
        },

        yearFormat (yearPick) {
            const [year] = yearPick.split('-')
            const BEYear = parseInt(year) + 543
            return `${BEYear}`
        },

        async getBranch () {
            this.branch = parseInt(localStorage.getItem('currentBranchId'))
            if (this.branch) {
                await this.getGroupHolidayPagination()
            }
        },

        setMinDate () {
            const now = new Date()
            const nextDate = now.setDate(now.getDate() + 1)
            return new Date(nextDate).toISOString().substr(0, 10)
        },

        checkPermission () {
            this.permissionCreate = this.$util.checkPermission(permissionConstant.groupHoliday.create)
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.groupHoliday.update)
            this.permissionDelete = this.$util.checkPermission(permissionConstant.groupHoliday.delete)
        },

        passedDate (item) {
            const dateObj = this.$util.dateStringToDateObj(item.date)
            const now = new Date()

            if (now.getTime() > dateObj.getTime()) {
                return false
            }
            return true
        }
    },

    async mounted () {
        await this.getCompanyHolidays()
        await this.getBranch()
        this.minDate = this.setMinDate()
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                if (this.branch) {
                    await this.getGroupHolidayPagination()
                }
            },
            deep: true
        },
        menu (val) {
            val && this.$nextTick(() => (this.$refs.picker.activePicker = 'YEAR'))
        },
        'year' () {
            this.displayYear = this.yearFormat(this.year)
        }
    }
}
</script>

<style lang="scss" scoped>
.company-holiday-header {
    font-size: 24px;
}

.year-input {
    margin-left: 40px;
}

::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}

::v-deep .search-input .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}

::v-deep .v-input__prepend-inner {
    margin: auto !important;
}

</style>
